import React, { useState } from 'react';
import { Collapse } from '../common/Collapse';
import { AdminLineChart, type ChartDataset } from './charts/AdminLineChart';

// ============================================================================
// Types
// ============================================================================

export interface QuickAction {
  label: string;
  href: string;
  icon: string;
  variant: 'primary' | 'secondary' | 'success' | 'danger' | 'warning' | 'info';
  devOnly?: boolean;
}

export interface StatCard {
  title: string;
  value: number | string;
  subtitle: string;
  icon: string;
  iconColor: string;
  borderColor?: string;
}

export interface EngagementStat {
  label: string;
  value: number | string;
  color: string;
}

export interface ChartData {
  [date: string]: number;
}

export interface AdminDashboardProps {
  // Quick Actions
  quickActions: QuickAction[];
  moreActions: QuickAction[];
  isDevelopment: boolean;

  // Stats
  appStats: StatCard[];
  aiStats: StatCard[];
  engagementStats: EngagementStat[];

  // Error monitoring
  errorsToday: number;
  totalErrors: number;
  errorLogsPath: string;

  // Chart data
  userGrowthData: ChartData;
  activeUsersData: ChartData;
  continuousActiveUsersData: ChartData;

  // Labels
  labels: {
    title: string;
    subtitle: string;
    quickActionsTitle: string;
    moreActionsLabel: string;
    activeUsersChartTitle: string;
    continuousActiveUsersChartTitle: string;
    continuousActiveUsersSubtitle: string;
    userEngagementTitle: string;
    userGrowthChartTitle: string;
    errorAlertSingular: string;
    errorAlertPlural: string;
    totalErrorsLabel: string;
  };
}

// ============================================================================
// Helper Components
// ============================================================================

function ActionButton({ action }: { action: QuickAction }) {
  const variantClasses: Record<string, string> = {
    primary: 'btn-primary',
    secondary: 'btn-secondary',
    success: 'btn-success',
    danger: 'btn-danger',
    warning: 'btn-warning',
    info: 'btn-info',
  };

  return (
    <a href={action.href} className={`btn ${variantClasses[action.variant]}`}>
      <i className={`bi bi-${action.icon} me-1`} />
      {action.label}
    </a>
  );
}

function StatCardComponent({ stat }: { stat: StatCard }) {
  const borderClass = stat.borderColor ? `border-${stat.borderColor}` : '';

  return (
    <div className={`card shadow-sm h-100 ${borderClass}`}>
      <div className="card-body">
        <div className="d-flex align-items-center justify-content-between mb-2">
          <h6 className="text-muted mb-0">{stat.title}</h6>
          <i
            className={`bi bi-${stat.icon} text-${stat.iconColor}`}
            style={{ fontSize: '1.5rem' }}
          />
        </div>
        <h2 className="mb-0">{typeof stat.value === 'number' ? stat.value.toLocaleString() : stat.value}</h2>
        <small className="text-muted">{stat.subtitle}</small>
      </div>
    </div>
  );
}

function EngagementStatBox({ stat }: { stat: EngagementStat }) {
  return (
    <div className="col-md-3">
      <div className="p-3 bg-light rounded text-center">
        <div className="text-muted small mb-1">{stat.label}</div>
        <div className={`h4 mb-0 text-${stat.color}`}>
          {typeof stat.value === 'number' ? stat.value.toLocaleString() : stat.value}
        </div>
      </div>
    </div>
  );
}

function ErrorAlert({
  errorsToday,
  totalErrors,
  errorLogsPath,
  labels,
}: {
  errorsToday: number;
  totalErrors: number;
  errorLogsPath: string;
  labels: AdminDashboardProps['labels'];
}) {
  if (errorsToday === 0) return null;

  const errorText = errorsToday === 1 ? labels.errorAlertSingular : labels.errorAlertPlural;

  return (
    <div className="row g-3 mb-4">
      <div className="col-12">
        <a href={errorLogsPath} className="text-decoration-none">
          <div className="card shadow-sm border-danger">
            <div className="card-body d-flex align-items-center justify-content-between py-3">
              <div className="d-flex align-items-center">
                <i
                  className="bi bi-exclamation-triangle-fill text-danger me-3"
                  style={{ fontSize: '1.5rem' }}
                />
                <div>
                  <h6 className="mb-0 text-danger">
                    {errorsToday} {errorText}
                  </h6>
                  <small className="text-muted">
                    {totalErrors.toLocaleString()} {labels.totalErrorsLabel}
                  </small>
                </div>
              </div>
              <i className="bi bi-chevron-right text-muted" />
            </div>
          </div>
        </a>
      </div>
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export function AdminDashboard({
  quickActions,
  moreActions,
  isDevelopment,
  appStats,
  aiStats,
  engagementStats,
  errorsToday,
  totalErrors,
  errorLogsPath,
  userGrowthData,
  activeUsersData,
  continuousActiveUsersData,
  labels,
}: AdminDashboardProps) {
  const [showMoreActions, setShowMoreActions] = useState(false);

  // Filter actions based on development mode
  const filteredMoreActions = moreActions.filter(
    (action) => !action.devOnly || isDevelopment
  );

  // Transform chart data to datasets
  const formatChartData = (data: ChartData): ChartDataset => {
    const entries = Object.entries(data);
    return {
      labels: entries.map(([date]) => {
        const d = new Date(date);
        return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      }),
      values: entries.map(([, value]) => value),
    };
  };

  const userGrowthDataset = formatChartData(userGrowthData);
  const activeUsersDataset = formatChartData(activeUsersData);
  const continuousActiveUsersDataset = formatChartData(continuousActiveUsersData);

  return (
    <div>
      {/* Header */}
      <div className="mb-4">
        <h1 className="h2">
          <i className="bi bi-speedometer2 me-2" />
          {labels.title}
        </h1>
        <p className="text-muted">{labels.subtitle}</p>
      </div>

      {/* Quick Actions */}
      <div className="card shadow-sm mb-4">
        <div className="card-body">
          <h5 className="card-title mb-3">
            <i className="bi bi-lightning-charge me-2" />
            {labels.quickActionsTitle}
          </h5>
          <div className="d-flex flex-wrap gap-2">
            {quickActions.map((action, index) => (
              <ActionButton key={index} action={action} />
            ))}
          </div>

          {/* More Actions Collapse */}
          <div>
            <button
              className="btn btn-outline-secondary mt-2"
              type="button"
              onClick={() => setShowMoreActions(!showMoreActions)}
              aria-expanded={showMoreActions}
            >
              <i className="bi bi-three-dots me-1" />
              {labels.moreActionsLabel}
            </button>
            <Collapse isOpen={showMoreActions}>
              <div className="d-flex flex-wrap gap-2 mt-2">
                {filteredMoreActions.map((action, index) => (
                  <ActionButton key={index} action={action} />
                ))}
              </div>
            </Collapse>
          </div>
        </div>
      </div>

      {/* App Stats */}
      <div className="row g-3 mb-4">
        {appStats.map((stat, index) => (
          <div key={index} className="col-md-6 col-xl-3">
            <StatCardComponent stat={stat} />
          </div>
        ))}
      </div>

      {/* AI Stats */}
      <div className="row g-3 mb-4">
        {aiStats.map((stat, index) => (
          <div key={index} className="col-md-6 col-xl-3">
            <StatCardComponent stat={stat} />
          </div>
        ))}
      </div>

      {/* Error Monitoring */}
      <ErrorAlert
        errorsToday={errorsToday}
        totalErrors={totalErrors}
        errorLogsPath={errorLogsPath}
        labels={labels}
      />

      {/* Active Users Charts */}
      <div className="row g-4">
        <div className="col-lg-6">
          <div className="card shadow-sm h-100">
            <div className="card-header bg-white">
              <h5 className="mb-0">
                <i className="bi bi-people me-2" />
                {labels.activeUsersChartTitle}
              </h5>
            </div>
            <div className="card-body">
              <AdminLineChart
                dataset={activeUsersDataset}
                color="rgb(25, 135, 84)"
                tooltipLabel="Active Users"
                height={200}
              />
            </div>
          </div>
        </div>

        <div className="col-lg-6">
          <div className="card shadow-sm h-100">
            <div className="card-header bg-white">
              <h5 className="mb-0">
                <i className="bi bi-person-check me-2" />
                {labels.continuousActiveUsersChartTitle}
              </h5>
              <small className="text-muted">{labels.continuousActiveUsersSubtitle}</small>
            </div>
            <div className="card-body">
              <AdminLineChart
                dataset={continuousActiveUsersDataset}
                color="rgb(111, 66, 193)"
                tooltipLabel="Continuous Active Users"
                height={200}
              />
            </div>
          </div>
        </div>
      </div>

      {/* User Engagement */}
      <div className="row g-4 mt-1">
        <div className="col-12">
          <div className="card shadow-sm">
            <div className="card-header bg-white">
              <h5 className="mb-0">
                <i className="bi bi-graph-up me-2" />
                {labels.userEngagementTitle}
              </h5>
            </div>
            <div className="card-body">
              <div className="row g-3">
                {engagementStats.map((stat, index) => (
                  <EngagementStatBox key={index} stat={stat} />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* User Growth Chart */}
      <div className="row g-4 mt-1">
        <div className="col-12">
          <div className="card shadow-sm">
            <div className="card-header bg-white">
              <h5 className="mb-0">
                <i className="bi bi-graph-up-arrow me-2" />
                {labels.userGrowthChartTitle}
              </h5>
            </div>
            <div className="card-body">
              <AdminLineChart
                dataset={userGrowthDataset}
                color="rgb(13, 110, 253)"
                tooltipLabel="New Users"
                height={80}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminDashboard;
